(() => {
  if (window.__mdProductUIFix) return;
  window.__mdProductUIFix = true;

  // Use the exact review cards already rendered from the store's review feed.
  document.addEventListener('click', event => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-md-view-all]') : null;
    if (!trigger) return;
    const root = trigger.closest('md-review-carousel');
    const track = root?.querySelector('.md-reviews__track');
    if (!root || !track) return;
    event.preventDefault();
    let panel = root.querySelector('.md-review-panel');
    if (!panel) {
      panel = document.createElement('dialog');
      panel.className = 'md-review-panel';
      panel.id = `${root.id}-all`;
      const headingId = `${panel.id}-title`;
      panel.setAttribute('aria-labelledby', headingId);
      const header = document.createElement('header');
      header.className = 'md-review-panel__header';
      const intro = document.createElement('div');
      const heading = document.createElement('h2');
      heading.className = 'md-review-panel__title';
      heading.id = headingId;
      heading.textContent = root.querySelector('.md-reviews__header h2')?.textContent || 'Customer reviews';
      const count = document.createElement('p');
      count.className = 'md-review-panel__count';
      const originals = [...track.querySelectorAll(':scope > .md-reviews__card')];
      count.textContent = (root.dataset.availableLabel || '{count} reviews available in this view').replace('{count}', String(originals.length));
      intro.append(heading, count);
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'md-review-panel__close';
      close.setAttribute('aria-label', root.dataset.closeLabel || 'Close');
      close.textContent = '\u00d7';
      header.append(intro, close);
      const grid = document.createElement('div');
      grid.className = 'md-review-panel__grid';
      originals.forEach(original => {
        const card = original.cloneNode(true);
        card.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        card.querySelector('.md-reviews__body')?.classList.add('is-expanded');
        card.querySelectorAll('[data-read-more]').forEach(node => node.remove());
        grid.append(card);
      });
      panel.append(header, grid);
      root.append(panel);
      const closePanel = () => {
        if (typeof panel.close === 'function') panel.close();
        else { panel.removeAttribute('open'); trigger.setAttribute('aria-expanded', 'false'); trigger.focus({preventScroll:true}); }
      };
      close.addEventListener('click', closePanel);
      panel.addEventListener('click', click => {
        if (click.target !== panel) return;
        const box = panel.getBoundingClientRect();
        if (click.clientX < box.left || click.clientX > box.right || click.clientY < box.top || click.clientY > box.bottom) closePanel();
      });
      panel.addEventListener('close', () => {
        trigger.setAttribute('aria-expanded', 'false');
        if (trigger.isConnected) trigger.focus({preventScroll:true});
      });
    }
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', 'true');
    if (!panel.open) {
      if (typeof panel.showModal === 'function') panel.showModal();
      else { panel.setAttribute('open', ''); panel.scrollIntoView({block:'start'}); }
    }
  });

  // Observe the native recommendation scroller, including asynchronously loaded slides.
  if (customElements.get('md-carousel-progress')) return;
  class MDCarouselProgress extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      queueMicrotask(() => this.connectScroller());
    }
    connectScroller() {
      if (!this.isConnected || !this._controller) return;
      this.scroller = this.parentElement?.querySelector('slideshow-component > slideshow-container > slideshow-slides');
      if (!this.scroller) { this.hidden = true; return; }
      const {signal} = this._controller;
      const queue = () => {
        cancelAnimationFrame(this._frame);
        this._frame = requestAnimationFrame(() => this.update());
      };
      this.scroller.addEventListener('scroll', queue, {passive:true, signal});
      this.scroller.addEventListener('load', queue, {capture:true, signal});
      window.addEventListener('resize', queue, {passive:true, signal});
      this.addEventListener('keydown', event => {
        const max = this.maximum();
        const step = this.scroller.clientWidth * .8;
        const rtl = getComputedStyle(this.scroller).direction === 'rtl';
        let next = this.position();
        if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = max;
        else if (event.key === 'ArrowRight') next += rtl ? -step : step;
        else if (event.key === 'ArrowLeft') next += rtl ? step : -step;
        else return;
        event.preventDefault();
        this.seek(next, true);
      }, {signal});
      const point = event => {
        const rect = this.getBoundingClientRect();
        let ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width)));
        if (getComputedStyle(this.scroller).direction === 'rtl') ratio = 1 - ratio;
        this.seek(ratio * this.maximum(), false);
      };
      this.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        this._pointer = event.pointerId;
        this.setPointerCapture(event.pointerId);
        point(event);
      }, {signal});
      this.addEventListener('pointermove', event => { if (this._pointer === event.pointerId) point(event); }, {signal});
      const release = () => { this._pointer = null; };
      this.addEventListener('pointerup', release, {signal});
      this.addEventListener('pointercancel', release, {signal});
      this.addEventListener('lostpointercapture', release, {signal});
      if ('ResizeObserver' in window) {
        this._resize = new ResizeObserver(queue);
        this._resize.observe(this.scroller);
        [...this.scroller.children].forEach(slide => this._resize.observe(slide));
      }
      this._mutations = new MutationObserver(queue);
      this._mutations.observe(this.scroller, {childList:true});
      document.fonts?.ready.then(() => { if (this.isConnected) queue(); });
      this.update();
    }
    maximum() { return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth); }
    position() { return Math.min(this.maximum(), Math.abs(this.scroller.scrollLeft)); }
    seek(value, animate) {
      const rtl = getComputedStyle(this.scroller).direction === 'rtl';
      const left = Math.max(0, Math.min(this.maximum(), value)) * (rtl ? -1 : 1);
      this.scroller.scrollTo({left, behavior:animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
    }
    update() {
      if (!this.isConnected || !this.scroller) return;
      const max = this.maximum();
      this.hidden = max < 2 || this.scroller.clientWidth === 0;
      if (this.hidden) return;
      const visible = Math.min(100, Math.max(5, this.scroller.clientWidth / this.scroller.scrollWidth * 100));
      const fraction = max ? this.position() / max : 0;
      this.style.setProperty('--md-progress-size', `${visible}%`);
      this.style.setProperty('--md-progress-position', `${fraction * (100 - visible)}%`);
      this.setAttribute('aria-valuenow', String(Math.round(fraction * 100)));
    }
    disconnectedCallback() {
      this._controller?.abort(); this._controller = null;
      this._resize?.disconnect(); this._mutations?.disconnect();
      cancelAnimationFrame(this._frame);
    }
  }
  customElements.define('md-carousel-progress', MDCarouselProgress);
})();
